import type { Document } from "../../core/document/Document";
import type { Layer, BlendMode, AdjustmentLayer, AdjustmentKind } from "../../core/document/Layer";
import type { TypeLayer } from "../../core/document/TypeLayer";
import { isTypeLayer } from "../../core/document/TypeLayer";
import { isShapeLayer } from "../../core/document/Layer";

const BLEND_MODES: Array<[string, string]> = [
  ["normal", "Normal"], ["dissolve", "Dissolve"],
  ["darken", "Darken"], ["multiply", "Multiply"], ["color-burn", "Color Burn"], ["linear-burn", "Linear Burn"],
  ["lighten", "Lighten"], ["screen", "Screen"], ["color-dodge", "Color Dodge"], ["linear-dodge", "Linear Dodge"],
  ["overlay", "Overlay"], ["soft-light", "Soft Light"], ["hard-light", "Hard Light"],
  ["vivid-light", "Vivid Light"], ["linear-light", "Linear Light"], ["pin-light", "Pin Light"], ["hard-mix", "Hard Mix"],
  ["difference", "Difference"], ["exclusion", "Exclusion"],
  ["hue", "Hue"], ["saturation", "Saturation"], ["color", "Color"], ["luminosity", "Luminosity"],
];

export class PropertiesPanel {
  private container: HTMLElement;
  private bodyEl: HTMLElement;
  private onPatch: ((layerId: string, props: Record<string, unknown>) => void) | null = null;
  private onDocPatch: ((props: Record<string, unknown>) => void) | null = null;

  constructor() {
    this.container = document.createElement("div");
    this.container.className = "properties-panel";
    this.container.innerHTML = `
      <div class="panel-title">Properties</div>
      <div class="properties-body" id="properties-body"></div>
    `;
    this.bodyEl = this.container.querySelector(".properties-body")!;
  }

  setCallbacks(callbacks: {
    onPatch?: (layerId: string, props: Record<string, unknown>) => void;
    onDocPatch?: (props: Record<string, unknown>) => void;
  }): void {
    this.onPatch = callbacks.onPatch || null;
    this.onDocPatch = callbacks.onDocPatch || null;
  }

  update(doc: Document | null, activeLayer: Layer | null): void {
    if (!doc) {
      this.bodyEl.innerHTML = '<div class="muted" style="padding:16px;text-align:center">No document open</div>';
      return;
    }

    if (!activeLayer) {
      this._renderDocumentProps(doc);
      return;
    }

    if (isTypeLayer(activeLayer)) {
      this._renderTypeLayerProps(doc, activeLayer);
    } else if (isShapeLayer(activeLayer)) {
      this._renderShapeLayerProps(doc, activeLayer);
    } else if (activeLayer.kind === "adjustment") {
      this._renderAdjustmentLayerProps(doc, activeLayer as AdjustmentLayer);
    } else if (activeLayer.kind === "smart-object") {
      this._renderSmartObjectProps(doc, activeLayer);
    } else {
      this._renderLayerProps(doc, activeLayer);
    }
  }

  private _renderDocumentProps(doc: Document): void {
    this.bodyEl.innerHTML = `
      <div class="prop-section">
        <div class="prop-section-title">Document</div>
        <div class="prop-row">
          <label>Name</label>
          <input type="text" class="prop-input" id="prop-doc-name" value="${this._esc(doc.name)}" />
        </div>
        <div class="prop-row">
          <label>Width</label>
          <input type="number" class="prop-input" id="prop-doc-w" value="${doc.width}" min="1" max="100000" />
        </div>
        <div class="prop-row">
          <label>Height</label>
          <input type="number" class="prop-input" id="prop-doc-h" value="${doc.height}" min="1" max="100000" />
        </div>
        <div class="prop-row">
          <label>Resolution</label>
          <input type="number" class="prop-input" id="prop-doc-dpi" value="${doc.dpi || 72}" min="1" max="2400" />
        </div>
        <div class="prop-row">
          <label>Mode</label>
          <span class="prop-value">RGB/8</span>
        </div>
        <div class="prop-row">
          <label>Background</label>
          <div class="prop-color-swatch" id="prop-doc-bg" style="background:#fff"></div>
        </div>
      </div>
    `;

    const nameInput = this.bodyEl.querySelector("#prop-doc-name") as HTMLInputElement;
    nameInput?.addEventListener("change", () => {
      if (this.onDocPatch) this.onDocPatch({ name: nameInput.value });
    });

    const wInput = this.bodyEl.querySelector("#prop-doc-w") as HTMLInputElement;
    const hInput = this.bodyEl.querySelector("#prop-doc-h") as HTMLInputElement;
    const dpiInput = this.bodyEl.querySelector("#prop-doc-dpi") as HTMLInputElement;

    [wInput, hInput, dpiInput].forEach(input => {
      input?.addEventListener("change", () => {
        if (this.onDocPatch) {
          const props: Record<string, unknown> = {};
          if (wInput.value) props.width = parseInt(wInput.value);
          if (hInput.value) props.height = parseInt(hInput.value);
          if (dpiInput.value) props.dpi = parseInt(dpiInput.value);
          if (Object.keys(props).length) this.onDocPatch(props);
        }
      });
    });
  }

  private _renderLayerProps(doc: Document, layer: Layer): void {
    const blendOptions = BLEND_MODES.map(([v, l]) => `<option value="${v}"${layer.blendMode === v ? " selected" : ""}>${l}</option>`).join("");
    const opacityPct = Math.round((layer.opacity ?? 1) * 100);
    const fillPct = Math.round((layer.fillOpacity ?? 1) * 100);

    this.bodyEl.innerHTML = `
      <div class="prop-section">
        <div class="prop-section-title">Layer</div>
        <div class="prop-row">
          <label>Name</label>
          <input type="text" class="prop-input" id="prop-layer-name" value="${this._esc(layer.name)}" />
        </div>
        <div class="prop-row">
          <label>Opacity</label>
          <input type="range" class="prop-slider" id="prop-layer-opacity" min="0" max="100" value="${opacityPct}" />
          <span class="prop-value" id="prop-layer-opacity-val">${opacityPct}%</span>
        </div>
        <div class="prop-row">
          <label>Fill</label>
          <input type="range" class="prop-slider" id="prop-layer-fill" min="0" max="100" value="${fillPct}" />
          <span class="prop-value" id="prop-layer-fill-val">${fillPct}%</span>
        </div>
        <div class="prop-row">
          <label>Blend Mode</label>
          <select class="prop-select" id="prop-layer-blend">${blendOptions}</select>
        </div>
      </div>
      <div class="prop-section">
        <div class="prop-section-title">Lock</div>
        <div class="prop-row">
          <label class="prop-checkbox"><input type="checkbox" id="prop-lock-trans"${layer.lockTransparency ? " checked" : ""} /> Transparency</label>
        </div>
        <div class="prop-row">
          <label class="prop-checkbox"><input type="checkbox" id="prop-lock-img"${layer.lockImage ? " checked" : ""} /> Image</label>
        </div>
        <div class="prop-row">
          <label class="prop-checkbox"><input type="checkbox" id="prop-lock-pos"${layer.lockPosition ? " checked" : ""} /> Position</label>
        </div>
        <div class="prop-row">
          <label class="prop-checkbox"><input type="checkbox" id="prop-lock-all"${layer.lockAll ? " checked" : ""} /> All</label>
        </div>
      </div>
      <div class="prop-section">
        <div class="prop-section-title">Position</div>
        <div class="prop-row">
          <label>X</label>
          <input type="number" class="prop-input" id="prop-layer-x" value="${Math.round(layer.x)}" />
        </div>
        <div class="prop-row">
          <label>Y</label>
          <input type="number" class="prop-input" id="prop-layer-y" value="${Math.round(layer.y)}" />
        </div>
      </div>
    `;

    this._bindLayerProps(layer);
  }

  private _renderTypeLayerProps(doc: Document, layer: TypeLayer): void {
    this._renderLayerProps(doc, layer);

    const section = this.bodyEl.querySelector(".prop-section:last-child")!;
    const typeSection = document.createElement("div");
    typeSection.className = "prop-section";
    typeSection.innerHTML = `
      <div class="prop-section-title">Character</div>
      <div class="prop-row">
        <label>Font</label>
        <input type="text" class="prop-input" id="prop-type-font" value="${this._esc(layer.fontFamily)}" />
      </div>
      <div class="prop-row">
        <label>Size</label>
        <input type="number" class="prop-input" id="prop-type-size" value="${layer.fontSize}" min="1" max="1296" />
      </div>
      <div class="prop-row">
        <label>Color</label>
        <div class="prop-color-swatch" id="prop-type-color" style="background:rgb(${layer.color.r},${layer.color.g},${layer.color.b})"></div>
      </div>
      <div class="prop-row">
        <label>Align</label>
        <select class="prop-select" id="prop-type-align">
          <option value="left"${layer.textAlign === "left" ? " selected" : ""}>Left</option>
          <option value="center"${layer.textAlign === "center" ? " selected" : ""}>Center</option>
          <option value="right"${layer.textAlign === "right" ? " selected" : ""}>Right</option>
        </select>
      </div>
    `;
    section.after(typeSection);

    const fontInput = this.bodyEl.querySelector("#prop-type-font") as HTMLInputElement;
    const sizeInput = this.bodyEl.querySelector("#prop-type-size") as HTMLInputElement;
    const alignSelect = this.bodyEl.querySelector("#prop-type-align") as HTMLSelectElement;

    fontInput?.addEventListener("change", () => {
      if (this.onPatch) this.onPatch(layer.id, { fontFamily: fontInput.value });
    });
    sizeInput?.addEventListener("change", () => {
      if (this.onPatch) this.onPatch(layer.id, { fontSize: parseInt(sizeInput.value) });
    });
    alignSelect?.addEventListener("change", () => {
      if (this.onPatch) this.onPatch(layer.id, { textAlign: alignSelect.value });
    });
  }

  private _renderShapeLayerProps(doc: Document, layer: Layer): void {
    this._renderLayerProps(doc, layer);

    const section = this.bodyEl.querySelector(".prop-section:last-child")!;
    const shapeSection = document.createElement("div");
    shapeSection.className = "prop-section";
    shapeSection.innerHTML = `
      <div class="prop-section-title">Shape</div>
      <div class="prop-row">
        <label>Fill</label>
        <div class="prop-color-swatch" id="prop-shape-fill" style="background:#4a90e2"></div>
      </div>
      <div class="prop-row">
        <label>Stroke</label>
        <div class="prop-color-swatch" id="prop-shape-stroke" style="background:#333"></div>
      </div>
      <div class="prop-row">
        <label>Stroke Width</label>
        <input type="number" class="prop-input" id="prop-shape-stroke-w" value="2" min="0" max="100" />
      </div>
      <div class="prop-row">
        <label>Corner Radius</label>
        <input type="number" class="prop-input" id="prop-shape-radius" value="0" min="0" max="500" />
      </div>
    `;
    section.after(shapeSection);
  }

  private _renderAdjustmentLayerProps(doc: Document, layer: AdjustmentLayer): void {
    this._renderLayerProps(doc, layer);

    const section = this.bodyEl.querySelector(".prop-section:last-child")!;
    const adjSection = document.createElement("div");
    adjSection.className = "prop-section";
    const kind = layer.adjustment;
    const params = layer.params || {};

    let fields = "";
    switch (kind) {
      case "brightness-contrast":
        fields = `
          <div class="prop-row"><label>Brightness</label><input type="range" class="prop-slider" id="prop-adj-brightness" min="-100" max="100" value="${params.brightness ?? 0}" /><span class="prop-value" id="prop-adj-brightness-val">${params.brightness ?? 0}</span></div>
          <div class="prop-row"><label>Contrast</label><input type="range" class="prop-slider" id="prop-adj-contrast" min="-100" max="100" value="${params.contrast ?? 0}" /><span class="prop-value" id="prop-adj-contrast-val">${params.contrast ?? 0}</span></div>
        `;
        break;
      case "hue-saturation":
        fields = `
          <div class="prop-row"><label>Hue</label><input type="range" class="prop-slider" id="prop-adj-hue" min="-180" max="180" value="${params.hue ?? 0}" /><span class="prop-value" id="prop-adj-hue-val">${params.hue ?? 0}</span></div>
          <div class="prop-row"><label>Saturation</label><input type="range" class="prop-slider" id="prop-adj-sat" min="-100" max="100" value="${params.saturation ?? 0}" /><span class="prop-value" id="prop-adj-sat-val">${params.saturation ?? 0}</span></div>
          <div class="prop-row"><label>Lightness</label><input type="range" class="prop-slider" id="prop-adj-light" min="-100" max="100" value="${params.lightness ?? 0}" /><span class="prop-value" id="prop-adj-light-val">${params.lightness ?? 0}</span></div>
        `;
        break;
      case "levels":
        fields = `
          <div class="prop-row"><label>Input Black</label><input type="range" class="prop-slider" id="prop-adj-iblack" min="0" max="255" value="${params.inputBlack ?? 0}" /><span class="prop-value">${params.inputBlack ?? 0}</span></div>
          <div class="prop-row"><label>Gamma</label><input type="range" class="prop-slider" id="prop-adj-gamma" min="0.1" max="10" step="0.01" value="${params.gamma ?? 1}" /><span class="prop-value">${params.gamma ?? 1}</span></div>
          <div class="prop-row"><label>Input White</label><input type="range" class="prop-slider" id="prop-adj-iwhite" min="0" max="255" value="${params.inputWhite ?? 255}" /><span class="prop-value">${params.inputWhite ?? 255}</span></div>
        `;
        break;
      case "exposure":
        fields = `
          <div class="prop-row"><label>Exposure</label><input type="range" class="prop-slider" id="prop-adj-exposure" min="-20" max="20" step="0.1" value="${params.exposure ?? 0}" /><span class="prop-value">${params.exposure ?? 0}</span></div>
          <div class="prop-row"><label>Offset</label><input type="range" class="prop-slider" id="prop-adj-offset" min="-1" max="1" step="0.01" value="${params.offset ?? 0}" /><span class="prop-value">${params.offset ?? 0}</span></div>
          <div class="prop-row"><label>Gamma</label><input type="range" class="prop-slider" id="prop-adj-egamma" min="0.1" max="10" step="0.01" value="${params.gamma ?? 1}" /><span class="prop-value">${params.gamma ?? 1}</span></div>
        `;
        break;
      case "vibrance":
        fields = `
          <div class="prop-row"><label>Vibrance</label><input type="range" class="prop-slider" id="prop-adj-vibrance" min="-100" max="100" value="${params.vibrance ?? 0}" /><span class="prop-value">${params.vibrance ?? 0}</span></div>
          <div class="prop-row"><label>Saturation</label><input type="range" class="prop-slider" id="prop-adj-vsat" min="-100" max="100" value="${params.saturation ?? 0}" /><span class="prop-value">${params.saturation ?? 0}</span></div>
        `;
        break;
      case "threshold":
        fields = `<div class="prop-row"><label>Level</label><input type="range" class="prop-slider" id="prop-adj-threshold" min="0" max="255" value="${params.level ?? 128}" /><span class="prop-value">${params.level ?? 128}</span></div>`;
        break;
      case "posterize":
        fields = `<div class="prop-row"><label>Levels</label><input type="range" class="prop-slider" id="prop-adj-posterize" min="2" max="255" value="${params.levels ?? 4}" /><span class="prop-value">${params.levels ?? 4}</span></div>`;
        break;
      default:
        fields = `<div class="prop-row"><span class="muted">${kind} adjustment layer</span></div>`;
    }

    adjSection.innerHTML = `<div class="prop-section-title">${this._adjustmentLabel(kind)}</div>${fields}`;
    section.after(adjSection);

    // Wire sliders
    adjSection.querySelectorAll(".prop-slider").forEach(slider => {
      const s = slider as HTMLInputElement;
      const valSpan = s.parentElement?.querySelector(".prop-value") as HTMLElement;
      s.addEventListener("input", () => {
        if (valSpan) valSpan.textContent = s.value;
      });
      s.addEventListener("change", () => {
        const key = s.id.replace("prop-adj-", "");
        const paramMap: Record<string, string> = {
          brightness: "brightness", contrast: "contrast",
          hue: "hue", sat: "saturation", light: "lightness",
          iblack: "inputBlack", gamma: "gamma", iwhite: "inputWhite",
          exposure: "exposure", offset: "offset", egamma: "gamma",
          vibrance: "vibrance", vsat: "saturation",
          threshold: "level", posterize: "levels",
        };
        const paramKey = paramMap[key] || key;
        if (this.onPatch) {
          const newParams = { ...params, [paramKey]: parseFloat(s.value) };
          this.onPatch(layer.id, { params: newParams });
        }
      });
    });
  }

  private _renderSmartObjectProps(doc: Document, layer: Layer): void {
    this._renderLayerProps(doc, layer);

    const section = this.bodyEl.querySelector(".prop-section:last-child")!;
    const soSection = document.createElement("div");
    soSection.className = "prop-section";
    soSection.innerHTML = `
      <div class="prop-section-title">Smart Object</div>
      <div class="prop-row">
        <button class="prop-btn" id="prop-so-edit">Edit Contents</button>
      </div>
      <div class="prop-row">
        <button class="prop-btn" id="prop-so-rasterize">Rasterize</button>
      </div>
      <div class="prop-row">
        <label>Transform</label>
        <span class="prop-value">${layer.kind === "smart-object" ? JSON.stringify((layer as any).transform || {}) : "N/A"}</span>
      </div>
    `;
    section.after(soSection);

    soSection.querySelector("#prop-so-edit")?.addEventListener("click", () => {
      alert("Smart Object editing is not yet implemented.");
    });
  }

  private _bindLayerProps(layer: Layer): void {
    const nameInput = this.bodyEl.querySelector("#prop-layer-name") as HTMLInputElement;
    nameInput?.addEventListener("change", () => {
      if (this.onPatch) this.onPatch(layer.id, { name: nameInput.value });
    });

    const opacitySlider = this.bodyEl.querySelector("#prop-layer-opacity") as HTMLInputElement;
    const opacityVal = this.bodyEl.querySelector("#prop-layer-opacity-val") as HTMLElement;
    opacitySlider?.addEventListener("input", () => {
      if (opacityVal) opacityVal.textContent = `${opacitySlider.value}%`;
    });
    opacitySlider?.addEventListener("change", () => {
      if (this.onPatch) this.onPatch(layer.id, { opacity: parseInt(opacitySlider.value) / 100 });
    });

    const fillSlider = this.bodyEl.querySelector("#prop-layer-fill") as HTMLInputElement;
    const fillVal = this.bodyEl.querySelector("#prop-layer-fill-val") as HTMLElement;
    fillSlider?.addEventListener("input", () => {
      if (fillVal) fillVal.textContent = `${fillSlider.value}%`;
    });
    fillSlider?.addEventListener("change", () => {
      if (this.onPatch) this.onPatch(layer.id, { fillOpacity: parseInt(fillSlider.value) / 100 });
    });

    const blendSelect = this.bodyEl.querySelector("#prop-layer-blend") as HTMLSelectElement;
    blendSelect?.addEventListener("change", () => {
      if (this.onPatch) this.onPatch(layer.id, { blendMode: blendSelect.value as BlendMode });
    });

    const xInput = this.bodyEl.querySelector("#prop-layer-x") as HTMLInputElement;
    const yInput = this.bodyEl.querySelector("#prop-layer-y") as HTMLInputElement;
    xInput?.addEventListener("change", () => {
      if (this.onPatch) this.onPatch(layer.id, { x: parseInt(xInput.value) });
    });
    yInput?.addEventListener("change", () => {
      if (this.onPatch) this.onPatch(layer.id, { y: parseInt(yInput.value) });
    });

    // Lock checkboxes
    const lockIds = ["prop-lock-trans", "prop-lock-img", "prop-lock-pos", "prop-lock-all"];
    const lockProps = ["lockTransparency", "lockImage", "lockPosition", "lockAll"];
    lockIds.forEach((id, i) => {
      const cb = this.bodyEl.querySelector(`#${id}`) as HTMLInputElement;
      cb?.addEventListener("change", () => {
        if (this.onPatch) this.onPatch(layer.id, { [lockProps[i]!]: cb.checked });
      });
    });
  }

  private _adjustmentLabel(kind: AdjustmentKind): string {
    const labels: Record<string, string> = {
      "brightness-contrast": "Brightness/Contrast",
      "levels": "Levels",
      "curves": "Curves",
      "hue-saturation": "Hue/Saturation",
      "color-balance": "Color Balance",
      "black-white": "Black & White",
      "photo-filter": "Photo Filter",
      "gradient-map": "Gradient Map",
      "invert": "Invert",
      "posterize": "Posterize",
      "threshold": "Threshold",
      "solid-color": "Solid Color",
      "exposure": "Exposure",
      "vibrance": "Vibrance",
    };
    return labels[kind] || kind;
  }

  private _esc(s: string): string {
    return s.replace(/"/g, "&quot;").replace(/</g, "&lt;");
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
  }

  destroy(): void {
    this.container.remove();
  }
}
